import type { ComponentChildren } from 'preact';
import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import { anchorOffset, resolveDialogAnchor } from '@/dialogAnchor';
import { useCheckoutPresentation } from '@/components/CheckoutPresentation';
import { StepIndicator } from '@/components/StepIndicator';
import { SandboxBanner } from '@/components/SandboxBanner';
import { useSupportEmail } from '@/supportContact';
import { isTestKeysBackend } from '@/session';
import { t, useT } from '@/i18n/t';

// Matches the .sm-overlay-closing / .sm-checkout-shell-closing animation
// duration in styles.css. The real onDismiss (which unmounts this surface) is
// deferred by this long so the exit animation gets to play instead of the
// surface just disappearing.
const DISMISS_ANIMATION_MS = 150;

function prefersReducedMotion(): boolean {
  // matchMedia is absent in some non-browser hosts; treat that as "motion
  // allowed" rather than throw (mirrors the same guard in preview.tsx).
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

interface Props {
  ariaLabel: string;
  currentStep: number;
  sidebar?: ComponentChildren;
  children: ComponentChildren;
  wide?: boolean;
  // Narrows the two-column shell so the main column lands at ~400px — the max
  // width Google Identity Services will render its "Continue with Google" button
  // at. The sign-in step uses it so the native buttons/form fill the column at
  // the same width as the Google button. Ignored without a sidebar (solo shell).
  narrow?: boolean;
  hideSteps?: boolean;
  // Steps-less screens (expedited checkout) pass their title so the shell can
  // inline it with the back arrow and test-mode pill on one row — the
  // sm-login-head pattern — instead of a chrome band above the heading.
  // Ignored when the step indicator renders (hideSteps unset).
  title?: ComponentChildren;
  // When set, the top toolbar shows a back arrow (left of the step indicator,
  // mirroring the close button on the right). Omitted when there is nowhere to
  // step back to.
  onBack?: () => void;
  backDisabled?: boolean;
  // When set, the shell is dismissible: renders a close button and closes on
  // Escape. The donation flow and a dismissible paywall pass it; a blocking
  // page gate omits it so it stays put.
  onDismiss?: () => void;
  onDismissRequested?: () => void;
}

export function CheckoutShell({ ariaLabel, currentStep, sidebar, children, wide = false, narrow = false, hideSteps = false, title, onBack, backDisabled = false, onDismiss, onDismissRequested }: Props) {
  useT();
  const { inline } = useCheckoutPresentation();
  const dismiss = inline ? undefined : onDismiss;
  const hasSidebar = Boolean(sidebar);
  // Provided by the checkout flow root; absent when the publisher has not set a
  // support email, in which case no contact line renders.
  const supportEmail = useSupportEmail();
  // Without a sidebar (plan picker, login) collapse to one centered column so
  // the layout never reserves an empty sidebar gutter.
  let classes = 'sm-checkout-shell sm-checkout-shell-solo';
  if (hasSidebar) {
    classes = 'sm-checkout-shell';
    if (wide) {
      classes += ' sm-checkout-shell-wide';
    } else if (narrow) {
      classes += ' sm-checkout-shell-narrow';
    }
  }

  // closing plays the exit animation while holding the surface mounted for one
  // more beat; requestDismiss is what every close affordance below calls
  // instead of onDismiss directly, so the animation always gets a chance to
  // run before the parent actually unmounts this surface.
  const [closing, setClosing] = useState(false);
  const dismissTimer = useRef<number | null>(null);
  // A ref, not the closing state above, guards against a double dismiss: two
  // synchronous clicks (e.g. Escape + close-button, or a double-click) both
  // run before Preact commits the setClosing(true) re-render, so a state read
  // in this same guard would see closing === false for both and fire
  // onDismiss twice. The ref updates immediately, so the second call sees it.
  const dismissed = useRef(false);

  const requestDismiss = () => {
    if (!dismiss || dismissed.current) {
      return;
    }
    dismissed.current = true;
    onDismissRequested?.();
    setClosing(true);
    if (prefersReducedMotion()) {
      dismiss();
      return;
    }
    dismissTimer.current = window.setTimeout(dismiss, DISMISS_ANIMATION_MS);
  };

  useEffect(() => {
    return () => {
      if (dismissTimer.current != null) window.clearTimeout(dismissTimer.current);
    };
  }, []);

  // Uses the CSS `translate` property, not `transform`, so it composes with the
  // slide-up/scale-out animations instead of being overridden by them.
  const overlayRef = useRef<HTMLDivElement>(null);
  const shellRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const anchor = inline ? null : resolveDialogAnchor();
    const overlay = overlayRef.current;
    const shell = shellRef.current;
    if (!anchor || !overlay || !shell) {
      return;
    }
    const place = () => {
      const r = anchor.getBoundingClientRect();
      const { x, y } = anchorOffset(
        { left: r.left, top: r.top, width: r.width, height: r.height },
        { width: shell.offsetWidth, height: shell.offsetHeight },
        { width: overlay.clientWidth, height: overlay.clientHeight },
      );
      shell.style.translate = x || y ? `${x}px ${y}px` : '';
    };
    place();
    window.addEventListener('resize', place);
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(place) : null;
    // The overlay resizes when the page scroll lock (applied after this
    // effect) removes a classic scrollbar; the anchor can resize on its own.
    ro?.observe(shell);
    ro?.observe(overlay);
    ro?.observe(anchor);
    return () => {
      window.removeEventListener('resize', place);
      ro?.disconnect();
    };
  }, [inline]);

  useEffect(() => {
    if (!dismiss) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') requestDismiss();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [dismiss, onDismissRequested]);

  return (
    <div ref={overlayRef} class={inline ? 'sm-inline' : `sm-overlay${closing ? ' sm-overlay-closing' : ''}`}>
      <div ref={shellRef} class={`${classes}${inline ? ' sm-checkout-shell-inline' : ''}${closing ? ' sm-checkout-shell-closing' : ''}`} role={inline ? 'region' : 'dialog'} aria-modal={inline ? undefined : 'true'} aria-label={ariaLabel}>
        {dismiss && (
          <button type="button" class="sm-overlay-close" aria-label={t('shell.close')} onClick={requestDismiss}>
            &times;
          </button>
        )}
        <main class="sm-checkout-main">
          {/* The toolbar row renders when a step has chrome to put in it: a
              back arrow, the step indicator, or — on a two-column shell in
              test-keys mode — the test-mode pill. The pill's floating placement
              (.sm-sandbox-row) is solo-only: it is positioned against the
              whole card, so on a two-column shell it would land in the
              sidebar (over the order-summary heading) instead of the main
              column. Outside test-keys mode the pill renders nothing, so a
              sidebar + hideSteps + no back still skips the row entirely.
              Steps-less screens that pass `title` inline it with the arrow
              and pill (sm-login-head pattern) — no chrome band above the
              heading. */}
          {(onBack || !hideSteps || (hasSidebar && isTestKeysBackend())) ? (
            <div class={`sm-main-head${hideSteps && title ? ' sm-main-head-inline' : ''}`}>
              {onBack && (
                <button type="button" class="sm-back" aria-label={t('common.back')} onClick={onBack} disabled={backDisabled}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                </button>
              )}
              {!hideSteps && <StepIndicator current={currentStep} total={3} />}
              {hideSteps && title}
              <SandboxBanner />
            </div>
          ) : (
            <>
              <SandboxBanner floating />
              {/* No toolbar row (no back, no steps, no sidebar pill): the
                  steps-less title falls back to the body. */}
              {hideSteps && title}
            </>
          )}
          {children}
          {supportEmail && (
            <p class="sm-support-contact">
              {t('shell.supportContact')}{' '}
              <a class="sm-support-contact-link" href={`mailto:${supportEmail}`}>{supportEmail}</a>
            </p>
          )}
        </main>

        {hasSidebar && (
          <aside class="sm-checkout-sidebar">
            {sidebar}
          </aside>
        )}
      </div>
    </div>
  );
}
